import { useState, useEffect } from 'react';
import { loadTossPayments, ANONYMOUS } from '@tosspayments/tosspayments-sdk';
import { C } from '../data';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabase';
import { fetchMyCoupons, redeemCoupon, fetchUserPoints, spendPointsForOrder } from '../api';
import Footer from './Footer';
import Img from './Img';

export default function CheckoutPage({ onGoHome, nav, onLogin, buyNowItem, onClearBuyNow, siteSettings }) {
  const { items: cartItems, total: cartTotal, clearCart } = useCart();
  const { user, profile } = useAuth();

  const items = buyNowItem ? [buyNowItem] : cartItems;
  const total = buyNowItem ? buyNowItem.price * (buyNowItem.quantity || 1) : cartTotal;
  const [form, setForm] = useState({
    name: '', phone: '', email: '', zip: '', address1: '', address2: '', note: '',
  });
  const [paymentMethod, setPaymentMethod] = useState(() => {
    const stored = sessionStorage.getItem('checkoutPaymentMethod');
    if (stored) { sessionStorage.removeItem('checkoutPaymentMethod'); return stored; }
    return 'bank';
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [availablePoints, setAvailablePoints] = useState(0);
  const [pointsInput, setPointsInput] = useState('');
  const pointsToUse = Math.min(parseInt(pointsInput || '0', 10) || 0, availablePoints);

  useEffect(() => {
    if (!profile && !user) return;
    setForm(prev => ({
      ...prev,
      name: prev.name || profile?.name || user?.user_metadata?.display_name || '',
      phone: prev.phone || profile?.phone || '',
      zip: prev.zip || profile?.zip || '',
      address1: prev.address1 || profile?.address1 || '',
      address2: prev.address2 || profile?.address2 || '',
    }));
  }, [profile, user]);

  useEffect(() => {
    if (!user) return;
    fetchUserPoints(user.id).then(s => setAvailablePoints(s?.available || 0)).catch(() => {});
  }, [user]);

  const openPostcode = () => {
    new window.daum.Postcode({
      oncomplete: (data) => {
        setForm(prev => ({ ...prev, zip: data.zonecode, address1: data.roadAddress || data.jibunAddress, address2: '' }));
      },
    }).open();
  };

  const [myCoupons, setMyCoupons] = useState([]);
  const [selectedCouponId, setSelectedCouponId] = useState('');

  useEffect(() => {
    if (!user) return;
    fetchMyCoupons(user.id).then(setMyCoupons).catch(() => {});
  }, [user]);

  const now = Date.now();
  const usableCoupons = myCoupons.filter(uc =>
    uc.status === 'unused' && uc.coupons?.active &&
    (!uc.coupons.expires_at || new Date(uc.coupons.expires_at).getTime() >= now) &&
    total >= (uc.coupons.min_order_amount || 0)
  );
  const selectedUserCoupon = usableCoupons.find(uc => uc.id === selectedCouponId);

  const calcDiscount = (uc) => {
    if (!uc) return 0;
    const c = uc.coupons;
    if (c.type === 'percent') {
      const raw = Math.floor(total * (c.value / 100));
      return c.max_discount ? Math.min(raw, c.max_discount) : raw;
    }
    return Math.min(c.value, total);
  };

  const discount = calcDiscount(selectedUserCoupon);

  // 장바구니 아이템에 shippingFee 정보가 있으면 그걸 우선 사용
  const allFreeShipping = items.length > 0 && items.every(i => (i.shippingFee ?? -1) === 0);
  const hasPaidShipping  = items.some(i => (i.shippingFee ?? 0) > 0);
  const shipping = allFreeShipping ? 0
    : hasPaidShipping ? Math.max(...items.map(i => i.shippingFee ?? 0))
    : total >= 100000 ? 0 : 3000;

  // 무료배송 상품인데 원래라면 배송비가 붙을 상황 → 생색내기용 절감액
  const shippingSaved = allFreeShipping && total < 100000 ? 3000 : 0;

  const grandTotal = Math.max(total + shipping - discount - pointsToUse, 0);

  const pm = { bank: true, card: false, naverPay: false, kakaoPay: false, ...(siteSettings?.paymentMethods || {}) };

  const set = key => e => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setLoading(true); setError('');
    try {
      const isToss = paymentMethod === 'card';
      const isNaverPay = paymentMethod === 'naverpay';
      const isKakaoPay = paymentMethod === 'kakaopay';
      const orderId = crypto.randomUUID();

      // 무통장입금: 즉시 DB insert
      if (!isToss && !isNaverPay && !isKakaoPay) {
        const { error: orderErr } = await supabase.from('orders').insert({
          id: orderId,
          user_id: user?.id || null,
          items, shipping: form, total: grandTotal,
          status: 'pending',
          discount, coupon_code: selectedUserCoupon?.coupons?.code || selectedUserCoupon?.coupons?.title || null,
          payment_method: paymentMethod,
        });
        if (orderErr) throw orderErr;
        if (selectedUserCoupon) { await redeemCoupon(selectedUserCoupon.id, orderId).catch(() => {}); }
        if (pointsToUse > 0) { await spendPointsForOrder(orderId, pointsToUse).catch(() => {}); }
        if (buyNowItem) { onClearBuyNow?.(); } else { await clearCart(); }
        setDone(true);
        return;
      }

      // 네이버페이
      if (isNaverPay) {
        const clientId = process.env.REACT_APP_NAVERPAY_CLIENT_ID;
        const chainId  = process.env.REACT_APP_NAVERPAY_CHAIN_ID;
        if (!clientId || !chainId) {
          setError('네이버페이 설정이 필요합니다. (REACT_APP_NAVERPAY_CLIENT_ID, REACT_APP_NAVERPAY_CHAIN_ID)');
          setLoading(false);
          return;
        }
        if (!window.Naver?.Pay) {
          setError('네이버페이 SDK 로딩에 실패했습니다. 새로고침 후 다시 시도해 주세요.');
          setLoading(false);
          return;
        }
        sessionStorage.setItem('pendingNaverOrder', JSON.stringify({
          orderId,
          userId: user?.id || null,
          items,
          shipping: form,
          grandTotal,
          discount,
          couponCode: selectedUserCoupon?.coupons?.code || selectedUserCoupon?.coupons?.title || null,
          userCouponId: selectedUserCoupon?.id || null,
          pointsToUse,
          isBuyNow: !!buyNowItem,
        }));
        const oPay = window.Naver.Pay.create({
          mode: process.env.REACT_APP_NAVERPAY_ENV || 'development',
          clientId,
          chainId,
        });
        oPay.open({
          merchantPayKey: orderId,
          productName: items.length === 1 ? items[0].productName : `UNDERLOOKED 주문 (${items.length}건)`,
          totalPayAmount: grandTotal,
          taxScopeAmount: grandTotal,
          taxExScopeAmount: 0,
          returnUrl: `${window.location.origin}/payment/naver-result`,
          merchantUserKey: user?.id || undefined,
        });
        return;
      }

      // 카카오페이: 추후 연동
      if (isKakaoPay) {
        alert('카카오페이 연동 준비 중입니다.');
        setLoading(false);
        return;
      }

      // 카드(Toss): 결제 성공 후 PaymentResultPage에서 DB insert
      sessionStorage.setItem('pendingOrder', JSON.stringify({
        orderId,
        userId: user?.id || null,
        items,
        shipping: form,
        grandTotal,
        discount,
        couponCode: selectedUserCoupon?.coupons?.code || selectedUserCoupon?.coupons?.title || null,
        userCouponId: selectedUserCoupon?.id || null,
        pointsToUse,
        isBuyNow: !!buyNowItem,
      }));

      const clientKey = process.env.REACT_APP_TOSS_CLIENT_KEY || 'test_ck_D5GePWvyJnrK0W0k6q8gLzN97Eoq';
      const tossPayments = await loadTossPayments(clientKey);
      const payment = tossPayments.payment({ customerKey: user?.id || ANONYMOUS });

      await payment.requestPayment({
        method: 'CARD',
        amount: { currency: 'KRW', value: grandTotal },
        orderId,
        orderName: `UNDERLOOKED 주문 (${items.length}건)`,
        successUrl: `${window.location.origin}/payment/success`,
        failUrl: `${window.location.origin}/payment/fail`,
        customerEmail: user?.email || form.email || undefined,
        customerName: form.name,
        customerMobilePhone: form.phone?.replace(/-/g, '') || undefined,
      });
      // requestPayment redirects — 이후 코드 실행 안 됨
    } catch (err) {
      if (err?.code === 'USER_CANCEL') {
        // 사용자가 직접 닫은 경우 — 에러 표시 안 함
      } else if (err?.message?.includes('enum')) {
        setError('해당 결제 수단은 실제 계약 후 사용 가능합니다. 현재 테스트 환경에서는 카드 결제만 지원됩니다.');
      } else {
        setError(`결제 처리 중 오류가 발생했습니다. (${err?.message || err?.code})`);
      }
      setLoading(false);
    }
  };

  if (done) return (
    <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:24, padding:40 }}>
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>
      <span style={{ fontFamily:"'DM Serif Display',serif", fontSize:32, letterSpacing:2 }}>주문 완료</span>
      <p style={{ fontFamily:"'Pretendard',sans-serif", fontSize:13, color:C.muted, textAlign:'center', lineHeight:2.2 }}>
        주문이 접수되었습니다.<br/>확인 후 배송 안내 이메일을 드리겠습니다.
      </p>
      <button onClick={onGoHome} style={{ padding:'12px 40px', background:C.ink, color:C.bg, border:'none', cursor:'pointer', fontFamily:"'Pretendard',sans-serif", fontWeight:500, fontSize:11, letterSpacing:3 }}>
        홈으로
      </button>
    </div>
  );

  return (
    <>
      <div style={{ maxWidth:1100, margin:'0 auto', padding:'var(--page-top, 100px) 20px 0' }}>
        <h1 style={{ fontFamily:"'DM Serif Display',serif", fontSize:clamp(24,32), marginBottom:48 }}>Checkout</h1>

        {items.length === 0 ? (
          <div style={{ textAlign:'center', padding:'80px 0' }}>
            <p style={{ fontFamily:"'Pretendard',sans-serif", fontSize:13, color:C.muted }}>장바구니가 비어있습니다.</p>
          </div>
        ) : (
          <div className="checkout-grid" style={{ display:'grid', gridTemplateColumns:'1fr min(400px,45%)', gap:60, alignItems:'start' }}>
            {/* Form */}
            <form onSubmit={handleSubmit}>
              {/* 비회원 안내 배너 */}
              {!user && (
                <div style={{
                  display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10,
                  padding:'14px 18px', marginBottom:28,
                  background:'#F7F5F2', border:'1px solid #E0DDD9',
                  fontFamily:"'Pretendard',sans-serif",
                }}>
                  <div>
                    <p style={{ fontSize:12, fontWeight:600, color:'#1A1A1A', marginBottom:3 }}>비회원으로 주문하기</p>
                    <p style={{ fontSize:11, color:'#888', lineHeight:1.7 }}>로그인 없이도 주문 가능합니다. 로그인하면 주문 내역 조회와 쿠폰 혜택을 받으실 수 있어요.</p>
                  </div>
                  <button type="button" onClick={onLogin} style={{
                    padding:'8px 18px', background:'#1A1A1A', color:'#fff',
                    border:'none', cursor:'pointer',
                    fontFamily:"'Pretendard',sans-serif", fontSize:11, fontWeight:500, letterSpacing:1.5,
                    whiteSpace:'nowrap', flexShrink:0,
                  }}>로그인</button>
                </div>
              )}

              <p style={{ fontFamily:"'Pretendard',sans-serif", fontWeight:500, fontSize:10, letterSpacing:3, marginBottom:20 }}>배송 정보</p>
              <div className="field-row" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                <Field label="이름 *" value={form.name} onChange={set('name')} required placeholder="홍길동" />
                <Field label="연락처 *" value={form.phone} onChange={set('phone')} required placeholder="010-0000-0000" />
              </div>
              {!user && (
                <div style={{ marginBottom:14 }}>
                  <Field label="이메일 (주문 확인용)" value={form.email} onChange={set('email')} type="email" placeholder="example@email.com" />
                </div>
              )}
              <div style={{ marginBottom:14 }}>
                <label className="adm-label">우편번호 *</label>
                <div style={{ display:'flex', gap:8 }}>
                  <input className="adm-input" style={{ maxWidth:120 }} value={form.zip} readOnly required placeholder="12345" />
                  <button type="button" onClick={openPostcode}
                    style={{ padding:'0 14px', background:'none', border:`1px solid ${C.border}`, cursor:'pointer', fontFamily:"'Pretendard',sans-serif", fontSize:10, letterSpacing:1.5, color:C.ink, whiteSpace:'nowrap', transition:'border-color .15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = C.ink}
                    onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
                    주소 검색
                  </button>
                </div>
              </div>
              <div style={{ marginBottom:14 }}>
                <label className="adm-label">기본 주소 *</label>
                <input className="adm-input" value={form.address1} readOnly required placeholder="주소 검색 후 자동 입력" />
              </div>
              <div style={{ marginBottom:14 }}>
                <label className="adm-label">상세 주소</label>
                <input className="adm-input" value={form.address2} onChange={set('address2')} placeholder="101동 202호" />
              </div>
              <div style={{ marginBottom:28 }}>
                <label className="adm-label">배송 메모</label>
                <input className="adm-input" value={form.note} onChange={set('note')} placeholder="예: 문 앞에 놓아주세요" />
              </div>

              {user && (
                <div style={{ marginBottom:28 }}>
                  <label className="adm-label">쿠폰</label>
                  <select className="adm-input" value={selectedCouponId} onChange={e => setSelectedCouponId(e.target.value)}>
                    <option value="">쿠폰을 선택하세요{usableCoupons.length === 0 ? ' (사용 가능한 쿠폰 없음)' : ''}</option>
                    {usableCoupons.map(uc => (
                      <option key={uc.id} value={uc.id}>
                        {uc.coupons.title} ({uc.coupons.type === 'percent' ? `${uc.coupons.value}%` : `₩${uc.coupons.value.toLocaleString()}`} 할인)
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {user && availablePoints > 0 && (
                <div style={{ marginBottom:28 }}>
                  <label className="adm-label">
                    적립금 사용
                    <span style={{ fontWeight:400, color:C.muted, marginLeft:8 }}>
                      사용 가능: ₩{availablePoints.toLocaleString()}
                    </span>
                  </label>
                  <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                    <input
                      className="adm-input"
                      type="number"
                      min="0"
                      max={availablePoints}
                      value={pointsInput}
                      onChange={e => setPointsInput(e.target.value)}
                      placeholder="0"
                      style={{ maxWidth:160 }}
                    />
                    <button type="button"
                      onClick={() => setPointsInput(String(availablePoints))}
                      style={{ padding:'9px 14px', background:'none', border:`1px solid ${C.border}`,
                        fontFamily:"'Pretendard',sans-serif", fontSize:10, letterSpacing:1.5, cursor:'pointer',
                        color:C.ink, whiteSpace:'nowrap' }}>
                      전액 사용
                    </button>
                  </div>
                  {pointsToUse > 0 && (
                    <p style={{ fontFamily:"'Pretendard',sans-serif", fontSize:11, color:'#3D8B5C', marginTop:6 }}>
                      ₩{pointsToUse.toLocaleString()} 적립금 적용
                    </p>
                  )}
                </div>
              )}

              {(pm.card || pm.naverPay || pm.kakaoPay) && (
                <div style={{
                  display:'flex', alignItems:'flex-start', gap:10,
                  padding:'13px 16px', marginBottom:16,
                  background:'#FFFBF0', border:'1px solid #EDD98A',
                }}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink:0, marginTop:1 }}>
                    <circle cx="7" cy="7" r="6.5" stroke="#C9A800" strokeWidth="1"/>
                    <path d="M7 4v3.5M7 9.5v.5" stroke="#C9A800" strokeWidth="1.2" strokeLinecap="round"/>
                  </svg>
                  <p style={{ fontFamily:"'Pretendard',sans-serif", fontSize:11, color:'#7A6200', lineHeight:1.7, margin:0 }}>
                    카드결제 및 간편결제는 현재 가맹점 심사 진행 중으로 일시적으로 이용이 제한될 수 있습니다.<br/>
                    원활한 주문을 위해 <strong>무통장입금</strong>을 이용해 주시면 감사하겠습니다.
                  </p>
                </div>
              )}

              <div style={{ padding:'18px 20px', background:C.bg2, border:`1px solid ${C.border}`, marginBottom:28 }}>
                <p style={{ fontFamily:"'Pretendard',sans-serif", fontWeight:500, fontSize:10, letterSpacing:2, marginBottom:12 }}>결제 수단</p>
                {[
                  { value: 'bank',     label: '무통장 입금 (새마을금고)', show: pm.bank },
                  { value: 'card',     label: '카드결제 (토스페이먼츠)',    show: pm.card },
                  { value: 'naverpay', label: '네이버페이 간편결제',        show: pm.naverPay },
                  { value: 'kakaopay', label: '카카오페이 간편결제',        show: pm.kakaoPay },
                ].filter(m => m.show).map(({ value, label }) => (
                  <div key={value} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8, cursor:'pointer' }}
                    onClick={() => setPaymentMethod(value)}>
                    <input type="radio" name="payment" value={value}
                      checked={paymentMethod === value} onChange={() => setPaymentMethod(value)}
                      style={{ accentColor:C.accent, cursor:'pointer' }} />
                    <span style={{ fontFamily:"'Pretendard',sans-serif", fontSize:12, color:C.ink }}>{label}</span>
                  </div>
                ))}
                {paymentMethod === 'bank' && (
                  <div style={{ background:C.surface, border:`1px solid ${C.border}`, padding:'12px 14px', marginTop:10 }}>
                    <p style={{ fontFamily:"'Pretendard',sans-serif", fontSize:10, color:C.muted, marginBottom:6 }}>입금 계좌 정보</p>
                    <p style={{ fontFamily:"'Pretendard',sans-serif", fontSize:12, color:C.ink, lineHeight:2, margin:0 }}>
                      새마을금고 9003-2286-3092-1<br/>
                      예금주 최순임
                    </p>
                  </div>
                )}
              </div>

              {error && <p style={{ fontFamily:"'Pretendard',sans-serif", fontSize:12, color:'#C94040', marginBottom:16 }}>{error}</p>}

              <button type="submit" disabled={loading} style={{
                width:'100%', padding:'16px 0', background:C.ink, color:C.bg, border:'none', cursor:'pointer',
                fontFamily:"'Pretendard',sans-serif", fontWeight:500, fontSize:11, letterSpacing:3,
                opacity:loading?.6:1, transition:'opacity .18s',
              }}>{loading ? '처리 중...' : paymentMethod === 'bank' ? '주문 확정' : paymentMethod === 'naverpay' ? '네이버페이로 결제' : paymentMethod === 'kakaopay' ? '카카오페이로 결제' : '카드 결제'}</button>
            </form>

            {/* Summary */}
            <div style={{ position:'sticky', top:100 }}>
              <p style={{ fontFamily:"'Pretendard',sans-serif", fontWeight:500, fontSize:10, letterSpacing:3, marginBottom:20 }}>주문 내역</p>
              <div style={{ border:`1px solid ${C.border}` }}>
                {items.map(item => (
                  <div key={item.key} style={{ display:'flex', gap:14, padding:'14px', borderBottom:`1px solid ${C.border}` }}>
                    <div style={{ width:56, flexShrink:0, aspectRatio:'3/4', overflow:'hidden', background:C.bg2 }}>
                      {item.img ? <img src={item.img} style={{ width:'100%', height:'100%', objectFit:'cover' }} alt="" /> : <Img style={{ width:'100%', height:'100%' }} label="" />}
                    </div>
                    <div style={{ flex:1 }}>
                      <p style={{ fontFamily:"'Presentation5',sans-serif", fontSize:12, marginBottom:3 }}>{item.productName}</p>
                      <p style={{ fontFamily:"'Pretendard',sans-serif", fontSize:10, color:C.muted, letterSpacing:1, marginBottom:5 }}>
                        {item.size}{item.colorName ? ` · ${item.colorName}` : ''} · {item.quantity}개
                      </p>
                      <p style={{ fontFamily:"'Presentation5',sans-serif", fontSize:12 }}>₩{(item.price * item.quantity).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
                <div style={{ padding:'16px 14px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                    <span style={{ fontFamily:"'Pretendard',sans-serif", fontSize:11, color:C.muted, letterSpacing:1 }}>상품 합계</span>
                    <span style={{ fontFamily:"'Presentation5',sans-serif", fontSize:12 }}>₩{total.toLocaleString()}</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                    <span style={{ fontFamily:"'Pretendard',sans-serif", fontSize:11, color:C.muted, letterSpacing:1 }}>배송비</span>
                    <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                      {shippingSaved > 0 && (
                        <span style={{ fontFamily:"'Presentation5',sans-serif", fontSize:11, color:'#BBB', textDecoration:'line-through' }}>
                          ₩{shippingSaved.toLocaleString()}
                        </span>
                      )}
                      <span style={{ fontFamily:"'Presentation5',sans-serif", fontSize:12, color: shipping === 0 ? '#3D8B5C' : C.ink, fontWeight: shipping === 0 ? 600 : 400 }}>
                        {shipping === 0 ? '무료' : `₩${shipping.toLocaleString()}`}
                      </span>
                    </span>
                  </div>
                  {shippingSaved > 0 && (
                    <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:8 }}>
                      <span style={{ fontFamily:"'Pretendard',sans-serif", fontSize:10, color:'#3D8B5C', letterSpacing:1 }}>
                        무료배송 혜택 적용 −₩{shippingSaved.toLocaleString()}
                      </span>
                    </div>
                  )}
                  {discount > 0 && (
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                      <span style={{ fontFamily:"'Pretendard',sans-serif", fontSize:11, color:'#C97A3D', letterSpacing:1 }}>쿠폰 할인</span>
                      <span style={{ fontFamily:"'Presentation5',sans-serif", fontSize:12, color:'#C97A3D' }}>−₩{discount.toLocaleString()}</span>
                    </div>
                  )}
                  {pointsToUse > 0 && (
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                      <span style={{ fontFamily:"'Pretendard',sans-serif", fontSize:11, color:'#3D8B5C', letterSpacing:1 }}>적립금 사용</span>
                      <span style={{ fontFamily:"'Presentation5',sans-serif", fontSize:12, color:'#3D8B5C' }}>−₩{pointsToUse.toLocaleString()}</span>
                    </div>
                  )}
                  <div style={{ height:1, background:C.border, margin:'12px 0' }} />
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline' }}>
                    <span style={{ fontFamily:"'Pretendard',sans-serif", fontWeight:500, fontSize:12, letterSpacing:1 }}>총 결제 금액</span>
                    <span style={{ fontFamily:"'Presentation5',sans-serif", fontSize:18 }}>₩{grandTotal.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer nav={nav} />
    </>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <label className="adm-label">{label}</label>
      <input className="adm-input" {...props} />
    </div>
  );
}

function clamp(min, max) {
  return `clamp(${min}px,3vw,${max}px)`;
}
